import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

const DOT_TONE_CLASS = {
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  neutral: "bg-fg-mute",
} as const;

const PILL_TONE_CLASS = {
  success: "bg-success-weak text-success",
  warning: "bg-warning-weak text-warning",
  danger: "bg-danger-weak text-danger",
  neutral: "bg-inset text-fg-soft",
} as const;

export type ConnectionTone = keyof typeof PILL_TONE_CLASS;

/**
 * Pill "ponto + rótulo" para estado de conexão/canal ao vivo (WhatsApp
 * conectado, WebSocket ao vivo, IA no ar — ver `AiStatus` para esse caso
 * específico). `role="status"`/`aria-live="polite"` porque o texto muda
 * sozinho, sem ação de quem está na tela — e o rótulo em texto sempre
 * acompanha a cor (estado nunca depende só do ponto colorido).
 *
 * `features/field/mediator/ConnectionStatus.tsx` tem uma variante própria
 * (pill contornada, maior, para leitura a distância em telão/kiosk) — é
 * intencionalmente diferente deste (contexto de tela cheia em TV, não de
 * painel administrativo) e não precisa migrar para este componente.
 */
export function ConnectionStatus({
  tone,
  children,
  className,
}: {
  tone: ConnectionTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      role="status"
      aria-live="polite"
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap",
        PILL_TONE_CLASS[tone],
        className,
      )}
    >
      <span className={cn("size-1.5 shrink-0 rounded-full", DOT_TONE_CLASS[tone])} aria-hidden="true" />
      {children}
    </span>
  );
}
